import { useMemo } from "react";

/**
 * PhoneCountryInput — v146.84
 *
 * Split phone-number input with a country-code dropdown + 10-digit
 * (or country-appropriate) national-number input. Used across every
 * admin/brand form that captures a phone: QR Sales employees, Admin
 * Staff, Brand staff invites — so users see one consistent pattern.
 *
 * The parent owns `value` (the full "+91 9876543210" string) and
 * `onChange(nextFullValue)`. This component splits it internally so
 * parents that already send `+91XXXXXXXXXX` upstream (e.g. WhatsApp
 * templates) keep working without a data-model change.
 *
 * Defaults to +91 (India). Mobile-first: dropdown + input stack on
 * `xs` and go side-by-side ≥`sm`.
 */
const COUNTRIES = [
  { code: "+91", flag: "🇮🇳", label: "India",  digits: 10 },
  { code: "+1",  flag: "🇺🇸", label: "USA / Canada", digits: 10 },
  { code: "+44", flag: "🇬🇧", label: "UK",     digits: 10 },
  { code: "+61", flag: "🇦🇺", label: "Australia", digits: 9 },
  { code: "+65", flag: "🇸🇬", label: "Singapore", digits: 8 },
  { code: "+971", flag: "🇦🇪", label: "UAE",   digits: 9 },
  { code: "+880", flag: "🇧🇩", label: "Bangladesh", digits: 10 },
  { code: "+94", flag: "🇱🇰", label: "Sri Lanka", digits: 9 },
  { code: "+977", flag: "🇳🇵", label: "Nepal", digits: 10 },
  { code: "+92", flag: "🇵🇰", label: "Pakistan", digits: 10 },
];

const DEFAULT_CC = "+91";

function splitPhone(value) {
  const raw = String(value || "").trim();
  if (!raw) return { cc: DEFAULT_CC, digits: "" };
  // Match longest country-code prefix that starts with `+`.
  const sorted = [...COUNTRIES].sort((a, b) => b.code.length - a.code.length);
  for (const c of sorted) {
    if (raw.startsWith(c.code)) {
      return { cc: c.code, digits: raw.slice(c.code.length).replace(/\D/g, "") };
    }
  }
  if (raw.startsWith("+")) {
    // Unknown country code — keep prefix through the first digits, up to 4.
    const m = raw.match(/^(\+\d{1,4})(.*)$/);
    if (m) return { cc: m[1], digits: (m[2] || "").replace(/\D/g, "") };
  }
  return { cc: DEFAULT_CC, digits: raw.replace(/\D/g, "") };
}

export default function PhoneCountryInput({
  value,
  onChange,
  placeholder = "10-digit number",
  required = false,
  disabled = false,
  testidPrefix = "phone",
  className = "",
  compact = false,
  size = "sm", // "sm" (default) | "lg" — lg = larger, always inline on mobile
  autoFocus = false,
}) {
  const { cc, digits } = useMemo(() => splitPhone(value), [value]);
  const current = COUNTRIES.find((c) => c.code === cc) || COUNTRIES[0];
  const maxDigits = current.digits;

  const emit = (nextCc, nextDigits) => {
    const clean = String(nextDigits || "").replace(/\D/g, "").slice(0, maxDigits);
    onChange(clean ? `${nextCc}${clean}` : "");
  };

  // v148.x — `size="lg"` variant: bigger fonts, always inline (so country
  // code sits in front of the 10-digit input on one line), narrower CC
  // pill so more room for the actual number. Used by the public Follow
  // modal where readability matters on small phones.
  const isLg = size === "lg";
  const wrapCls = isLg
    ? "flex gap-2 flex-row items-stretch"
    : "flex gap-2 flex-col sm:flex-row items-stretch";
  const ccCls = isLg
    ? "border-2 border-ink bg-white px-2 py-3 text-base font-mono font-bold w-[92px] shrink-0 focus:outline-none"
    : `border-2 border-ink bg-white ${compact ? "px-1.5 py-1 text-xs" : "px-2 py-2 text-sm"} font-mono font-bold sm:w-[110px] shrink-0 focus:outline-none`;
  const inputCls = isLg
    ? "border-2 border-ink px-3 py-3 text-lg font-mono tracking-wide flex-1 min-w-0 focus:outline-none"
    : `border-2 border-ink ${compact ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm"} font-mono flex-1 min-w-0 focus:outline-none`;

  return (
    <div
      className={`${wrapCls} ${className}`}
      data-testid={`${testidPrefix}-wrap`}
    >
      <select
        value={cc}
        onChange={(e) => emit(e.target.value, digits)}
        disabled={disabled}
        data-testid={`${testidPrefix}-cc`}
        aria-label="Country code"
        className={ccCls}
      >
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>
            {c.flag} {c.code}
          </option>
        ))}
      </select>
      <input
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        value={digits}
        onChange={(e) => emit(cc, e.target.value)}
        placeholder={placeholder}
        maxLength={maxDigits}
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        // v148.x — Native autofill hints so iOS/Android surface the
        // user's own phone number in the keyboard bar (auto-select).
        autoComplete="tel-national"
        name="tel-national"
        data-testid={`${testidPrefix}-digits`}
        aria-label="Phone number"
        className={inputCls}
      />
    </div>
  );
}

/** Utility exports for validation on the parent side. */
export function isValidPhone(value) {
  const { cc, digits } = splitPhone(value);
  const country = COUNTRIES.find((c) => c.code === cc);
  return !!(country && digits.length === country.digits);
}

export function normalisePhone(value) {
  const { cc, digits } = splitPhone(value);
  if (!digits) return "";
  return `${cc}${digits}`;
}
